import { isValidElement, type ComponentProps, type ReactNode, useMemo } from 'react';
import { type Stack } from 'expo-router';

// ─────────────────────────────────────────────────────────────────────────────

export const useHeaderButtonOptions = (params: Params) => {
  const { headerLeft, headerRight } = params;

  /**
   * NOTE: React Navigation's experimental iOS item API is the only way to pass
   *       `hidesSharedBackground` to the native bar button.
   *       We keep the stable options as fallbacks for other platforms.
   */
  return useMemo(
    () => ({
      headerLeftOptions: {
        headerLeft,
        unstable_headerLeftItems: headerLeft
          ? (props) => getHeaderItems(headerLeft(props))
          : undefined,
      } satisfies HeaderLeftOptions,

      headerRightOptions: {
        headerRight,
        unstable_headerRightItems: headerRight
          ? (props) => getHeaderItems(headerRight(props))
          : undefined,
      } satisfies HeaderRightOptions,
    }),
    [headerLeft, headerRight],
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getHeaderItems(element: ReactNode): HeaderItems {
  if (!isValidElement(element)) return [];

  // Keep Liquid Glass enabled while preventing iOS 26 from adding its rounded
  // background container to custom header buttons.
  return [{ type: 'custom', element, hidesSharedBackground: true }];
}

// ─── Types ───────────────────────────────────────────────────────────────────

type ScreenOptions = Extract<
  NonNullable<ComponentProps<typeof Stack>['screenOptions']>,
  Record<string, unknown>
>;

type Params = {
  headerLeft?: ScreenOptions['headerLeft'];
  headerRight?: ScreenOptions['headerRight'];
};

type HeaderLeftOptions = Pick<ScreenOptions, 'headerLeft' | 'unstable_headerLeftItems'>;
type HeaderRightOptions = Pick<ScreenOptions, 'headerRight' | 'unstable_headerRightItems'>;

type HeaderItems = ReturnType<NonNullable<ScreenOptions['unstable_headerLeftItems']>>;
